import * as Sentry from '@sentry/react'

/**
 * Extract a human-readable message from an unknown error.
 */
export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  if (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof (err as Record<string, unknown>).message === 'string'
  ) {
    return (err as Record<string, unknown>).message as string
  }
  return 'An unexpected error occurred.'
}

/**
 * Log an error to the console and report it to Sentry with feature context.
 * Always call this alongside any user-facing notification so the full
 * error (including stack trace) is available in the browser console.
 */
export function logError(feature: string, context: string, err: unknown): void {
  console.error(`[${feature}] ${context}:`, err)
  Sentry.withScope((scope) => {
    scope.setTag('feature', feature)
    scope.setTag('error.context', context)
    if (err instanceof Error) {
      Sentry.captureException(err)
    } else {
      const detail = typeof err === 'object' && err !== null ? JSON.stringify(err) : String(err)
      Sentry.captureMessage(`[${feature}] ${context}: ${detail}`, 'error')
    }
  })
}

/**
 * Log a warning to the console and report it to Sentry at warning level.
 * Use this for unexpected-but-valid states that need visibility without
 * polluting the error stream (e.g., empty search results that may
 * indicate a configuration issue).
 *
 * Filter in Sentry with `level:warning` to view these separately from errors.
 */
export function logWarning(feature: string, context: string, detail: string): void {
  console.warn(`[${feature}] ${context}: ${detail}`)
  Sentry.withScope((scope) => {
    scope.setTag('feature', feature)
    scope.setTag('warning.context', context)
    Sentry.captureMessage(`[${feature}] ${context}: ${detail}`, 'warning')
  })
}
